import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import CompleteIcon from '@material-ui/icons/CheckCircle';
import QueuedIcon from '@material-ui/icons/AccessTime';
import ErroredIcon from '@material-ui/icons/Error';
import {
  NumberFormat,
  Label,
  ModelConfigurationDetails,
  ModelSetModal
} from 'components';
import moment from 'moment';
import {
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CardHeader,
  Divider,
  colors,
  Link,
  Button,
  LinearProgress,
  Tooltip,
  TableSortLabel
} from '@material-ui/core';
import {
  findModelAnalysisComparison,
  findModelAnalysisComparisonSilently,
  recalculateModelAnalysisComparison,
  checkModelAnalysisStatus
} from 'actions';
import {
  CELL_MODELS,
  MODEL_ANALYSIS_SESSION_SOURCES,
  DEFAULT_SUB_MODEL_AREA_DESCRIPTIONS,
  MODEL_ANALYSIS_PROCESSING_STATUS
} from 'common/constants';
import { Link as RouterLink } from 'react-router-dom';
import ws from 'utils/ws';
import { Search } from 'components/SearchBar/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons';

const useStyles = makeStyles(theme => ({
  content: {
    padding: 0
  },
  container: {
    maxHeight: 400
  },
  columnSeparator: {
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[400],
    borderLeftStyle: 'dotted'
  },
  modelName: {
    maxWidth: 360,
    overflowX: 'hidden',
    textOverflow: 'ellipsis'
  },
  indicator: {
    cursor: 'help',
    display: 'inline-block',
    backgroundColor: colors.blue[600],
    height: 10,
    borderRadius: 6
  },
  actionButton: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1)
  },
  marginDetail: {
    marginLeft: theme.spacing(1)
  },
  modelEventCount: {
    color: colors.blueGrey[600]
  },
  subHeaderInfoIcon: {
    fontSize: 16,
    marginBottom: -3,
    color: colors.blue[600],
    marginRight: theme.spacing(0.5)
  },
  asWalkedInfoIcon: {
    cursor: 'help',
    fontSize: 16,
    marginBottom: -3,
    color: colors.blue[600],
    marginLeft: theme.spacing(1)
  },
  modelSetInfoIcon: {
    cursor: 'pointer',
    fontSize: 16,
    marginBottom: -3,
    color: colors.blue[600],
    marginLeft: theme.spacing(1)
  },
  valueInfoIcon: {
    fontSize: 16,
    marginBottom: -3,
    color: colors.blue[600],
    marginLeft: theme.spacing(1),
    cursor: 'pointer'
  },
  asWalkedRow: {
    backgroundColor: '#FFFDE5'
  },
  modelSetRow: {
    backgroundColor: 'rgba(211, 33, 155, 0.05)'
  },
  sortable: {
    fontSize: 14,
    opacity: 0.6,
    marginLeft: theme.spacing(1)
  }
}));

const formatAnalysisTimestamp = value => {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return moment(value);
  }

  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return moment(Number(value));
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

const ModelAnalysisComparison = props => {
  const { sourceType, sourceId, useSnowflakeApi = false } = props;

  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    modelAnalysisComparison: reduxModelAnalysisComparison,
    loading: reduxLoading,
    silentlyLoading,
    lastUpdatedTime: reduxLastUpdatedTime,
    status: reduxStatus
  } = useSelector(state => state.modelAnalysisComparison);

  const lastSeenSignatureRef = useRef(null);

  const [snowflakeAnalysis, setSnowflakeAnalysis] = useState({});
  const [snowflakeInitialLoading, setSnowflakeInitialLoading] = useState(
    useSnowflakeApi
  );
  const [snowflakeRefreshing, setSnowflakeRefreshing] = useState(false);
  const [snowflakeLastUpdatedTime, setSnowflakeLastUpdatedTime] = useState(null);
  const snowflakePollTimeoutRef = useRef(null);
  const snowflakeFetchGenerationRef = useRef(0);
  const fetchSnowflakeModelAnalysisRef = useRef(null);
  const snowflakeSignatureRef = useRef(null);
  const snowflakeSignatureLastChangedAtRef = useRef(null);

  const POLLING_FREQUENCY = 5000;
  const SNOWFLAKE_STABLE_STOP_MS = 90000;

  const clearSnowflakePollTimeout = () => {
    if (snowflakePollTimeoutRef.current) {
      clearTimeout(snowflakePollTimeoutRef.current);
      snowflakePollTimeoutRef.current = null;
    }
  };

  const hasSnowflakeTableData = analyses =>
    (analyses || []).some(ma => ma.eventCount > 0);

  const fetchSnowflakeModelAnalysis = useCallback(
    () =>
      ws.get(`/events/data_sets/${sourceId}/model_analysis`, {
        validateStatus: status => status === 200 || status === 202
      }),
    [sourceId]
  );

  fetchSnowflakeModelAnalysisRef.current = fetchSnowflakeModelAnalysis;

  /**
   * Lightweight polling: hits the projection-only /status endpoint instead of the
   * heavy /events/data_sets/{id}/model_analysis endpoint. The heavy endpoint takes
   * seconds to serve because Morphia lazy-loads 78×2 @Reference fields during
   * response serialization; /status uses a Mongo projection that skips the
   * embedded array entirely, so it's near-instant.
   *
   * Only refetch the heavy endpoint when the polled signature actually changes.
   */
  const fetchSnowflakeStatus = useCallback(
    () =>
      ws.get(
        `/model_analysis_comparisons/${sourceType}/${sourceId}/status`
      ),
    [sourceType, sourceId]
  );

  const handleSnowflakeStatusResponseRef = useRef(null);

  const scheduleSnowflakePoll = useCallback(() => {
    clearSnowflakePollTimeout();
    snowflakePollTimeoutRef.current = setTimeout(() => {
      fetchSnowflakeStatus()
        .then(response =>
          handleSnowflakeStatusResponseRef.current(response)
        )
        .catch(() => scheduleSnowflakePoll());
    }, POLLING_FREQUENCY);
  }, [fetchSnowflakeStatus]);

  const handleSnowflakeResponseRef = useRef(null);

  handleSnowflakeResponseRef.current = (response, { isPoll = false } = {}) => {
    if (!response) {
      if (isPoll || snowflakeInitialLoading) {
        scheduleSnowflakePoll();
      }
      return;
    }

    const result = response.data?.result || {};
    const hasTableData = hasSnowflakeTableData(result.modelConfigurationAnalyses);

    if (response.status === 200) {
      /**
       * The backend returns 200 as soon as it has cached data in Mongo, but a
       * background signature-validation job may still be refreshing that cache
       * from Snowflake. Keep polling until the response signature has been
       * stable for SNOWFLAKE_STABLE_STOP_MS milliseconds, so background updates
       * get picked up automatically without polling forever.
       */
      const newSignature = [
        result.calculatedTime,
        result.sourceWalkIdsHash
      ].join('|');
      const previousSignature = snowflakeSignatureRef.current;
      const signatureChanged =
        previousSignature !== null && newSignature !== previousSignature;
      const now = Date.now();

      setSnowflakeAnalysis(result);
      setSnowflakeLastUpdatedTime(new Date().getTime());
      setSnowflakeInitialLoading(false);
      setSnowflakeRefreshing(false);

      snowflakeSignatureRef.current = newSignature;

      if (previousSignature === null || signatureChanged) {
        snowflakeSignatureLastChangedAtRef.current = now;
        scheduleSnowflakePoll();
      } else {
        const stableFor = now - (snowflakeSignatureLastChangedAtRef.current || now);
        if (stableFor < SNOWFLAKE_STABLE_STOP_MS) {
          scheduleSnowflakePoll();
        } else {
          clearSnowflakePollTimeout();
        }
      }
      return;
    }

    if (response.status === 202) {
      setSnowflakeAnalysis(result);
      setSnowflakeInitialLoading(!hasTableData);
      setSnowflakeRefreshing(true);
      snowflakeSignatureLastChangedAtRef.current = Date.now();
      scheduleSnowflakePoll();
    }
  };

  /**
   * Handles the lightweight /status polling response. If the signature has
   * changed, kicks off a one-off heavy fetch to refresh the visible data.
   * Otherwise, checks the stability window and either schedules another
   * status poll or stops.
   *
   * Important: while the backend reports processingStatus=PROCESSING it is
   * actively recomputing from Snowflake and the signature won't advance until
   * the write completes. Keep polling (and reset the stability timer) so we
   * don't give up mid-refresh.
   */
  handleSnowflakeStatusResponseRef.current = response => {
    const status = response?.data?.result || {};
    const newSignature = [status.calculatedTime, status.sourceWalkIdsHash].join('|');
    const previousSignature = snowflakeSignatureRef.current;
    const signatureChanged =
      previousSignature !== null && newSignature !== previousSignature;
    const backendIsProcessing = status.processingStatus === 'PROCESSING';
    const now = Date.now();

    if (previousSignature === null || signatureChanged) {
      snowflakeSignatureLastChangedAtRef.current = now;
      snowflakeSignatureRef.current = newSignature;
      // Signature changed on the backend — pull the fresh full analysis.
      // The heavy response handler will update the table and continue polling.
      fetchSnowflakeModelAnalysisRef
        .current()
        .then(fullResponse =>
          handleSnowflakeResponseRef.current(fullResponse, { isPoll: true })
        )
        .catch(() => scheduleSnowflakePoll());
      return;
    }

    if (backendIsProcessing) {
      // Backend refresh in flight — keep polling and don't let the stability
      // window advance while we wait for the recompute to finish.
      snowflakeSignatureLastChangedAtRef.current = now;
      scheduleSnowflakePoll();
      return;
    }

    const stableFor = now - (snowflakeSignatureLastChangedAtRef.current || now);
    if (stableFor < SNOWFLAKE_STABLE_STOP_MS) {
      scheduleSnowflakePoll();
    } else {
      clearSnowflakePollTimeout();
    }
  };

  const modelAnalysisComparison = useSnowflakeApi
    ? snowflakeAnalysis
    : reduxModelAnalysisComparison;
  const loading = useSnowflakeApi ? snowflakeInitialLoading : reduxLoading;
  const lastUpdatedTime = useSnowflakeApi
    ? snowflakeLastUpdatedTime
    : reduxLastUpdatedTime;

  useEffect(() => {
    if (!useSnowflakeApi) {
      return undefined;
    }

    const generation = ++snowflakeFetchGenerationRef.current;
    let cancelled = false;

    setSnowflakeInitialLoading(true);
    setSnowflakeRefreshing(false);
    clearSnowflakePollTimeout();
    snowflakeSignatureRef.current = null;
    snowflakeSignatureLastChangedAtRef.current = null;

    fetchSnowflakeModelAnalysisRef
      .current()
      .then(response => {
        if (cancelled || generation !== snowflakeFetchGenerationRef.current) {
          return;
        }
        handleSnowflakeResponseRef.current(response);
      })
      .catch(() => {
        if (cancelled || generation !== snowflakeFetchGenerationRef.current) {
          return;
        }
        scheduleSnowflakePoll();
      });

    return () => {
      cancelled = true;
      snowflakeFetchGenerationRef.current += 1;
      clearSnowflakePollTimeout();
    };
  }, [useSnowflakeApi, sourceId, scheduleSnowflakePoll]);

  useEffect(() => {
    if (useSnowflakeApi) {
      return undefined;
    }

    let mounted = true;

    if (mounted) {
      dispatch(findModelAnalysisComparison(sourceType, sourceId));
    }

    return () => {
      mounted = false;
    };
  }, [useSnowflakeApi, sourceType, sourceId, dispatch]);

  /**
   * Track the signature (calculatedTime + sourceWalkIdsHash) of the analysis
   * currently displayed. When the lightweight status poll shows a different
   * signature, the cached analysis on the server has been rebuilt (e.g. the
   * Snowflake refresh finished) — refetch the full document once, so the UI
   * updates without a manual page refresh.
   */
  useEffect(() => {
    if (useSnowflakeApi) {
      return undefined;
    }
    if (!modelAnalysisComparison || !modelAnalysisComparison.calculatedTime) {
      return undefined;
    }
    const currentSignature = [
      modelAnalysisComparison.calculatedTime,
      modelAnalysisComparison.sourceWalkIdsHash
    ].join('|');
    lastSeenSignatureRef.current = currentSignature;
    return undefined;
  }, [useSnowflakeApi, modelAnalysisComparison]);

  useEffect(() => {
    if (useSnowflakeApi || loading) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      dispatch(checkModelAnalysisStatus(sourceType, sourceId));
    }, POLLING_FREQUENCY);

    return () => clearInterval(intervalId);
  }, [useSnowflakeApi, loading, sourceType, sourceId, dispatch]);

  useEffect(() => {
    if (useSnowflakeApi || loading || silentlyLoading) {
      return undefined;
    }
    if (!reduxStatus || !reduxStatus.calculatedTime) {
      return undefined;
    }

    const polledSignature = [
      reduxStatus.calculatedTime,
      reduxStatus.sourceWalkIdsHash
    ].join('|');

    if (
      lastSeenSignatureRef.current &&
      polledSignature !== lastSeenSignatureRef.current
    ) {
      dispatch(findModelAnalysisComparisonSilently(sourceType, sourceId));
    }
    return undefined;
  }, [useSnowflakeApi, reduxStatus, loading, silentlyLoading, sourceType, sourceId, dispatch]);

  const [detailsModelConfiguration, setDetailsModelConfiguration] = useState(
    null
  );
  const [detailsModelSet, setDetailsModelSet] = useState(null);
  const [modelSearch, setModelSearch] = useState('');
  const [columnSort, setColumnSort] = useState({
    column: 'scoreRank',
    direction: 'asc'
  });

  const displaySessionCount =
    sourceType === MODEL_ANALYSIS_SESSION_SOURCES.SESSION ||
    sourceType === MODEL_ANALYSIS_SESSION_SOURCES.SESSION_GROUP;
  const displayCustomerEventCount =
    sourceType === MODEL_ANALYSIS_SESSION_SOURCES.CUSTOMER_EVENT ||
    sourceType === MODEL_ANALYSIS_SESSION_SOURCES.SESSION_GROUP;
  const sortedAnalyses = (
    modelAnalysisComparison.modelConfigurationAnalyses || []
  )
    .filter(
      ma =>
        modelSearch.length === 0 ||
        (ma.modelSet?.name &&
          ma.modelSet.name.toUpperCase().indexOf(modelSearch.toUpperCase()) >
            -1) ||
        ((ma.modelConfiguration?.displayName || ma.modelConfiguration?.name) &&
          (ma.modelConfiguration.displayName || ma.modelConfiguration.name)
            .toUpperCase()
            .indexOf(modelSearch.toUpperCase()) > -1)
    )
    .filter(ma => ma.eventCount > 0);

  const firstAnalysis = sortedAnalyses[0];

  const completeCount = modelAnalysisComparison.modelConfigurationAnalyses
    ? modelAnalysisComparison.modelConfigurationAnalyses.filter(
        ma => ma.calculated
      ).length
    : 0;
  const incompleteCount = modelAnalysisComparison.modelConfigurationAnalyses
    ? modelAnalysisComparison.modelConfigurationAnalyses.filter(
        ma => !ma.calculated
      ).length
    : 0;
  const totalCount = modelAnalysisComparison.modelConfigurationAnalyses
    ? modelAnalysisComparison.modelConfigurationAnalyses.length
    : 0;

  sortedAnalyses.sort((analysis1, analysis2) => {
    return (
      (analysis1.scoreRank !== null
        ? analysis1.scoreRank
        : Number.MAX_SAFE_INTEGER) -
      (analysis2.scoreRank !== null
        ? analysis2.scoreRank
        : Number.MAX_SAFE_INTEGER)
    );
  });

  if (columnSort && columnSort.column) {
    sortedAnalyses.sort((a, b) => {
      if (a[columnSort.column] < b[columnSort.column]) {
        return columnSort.direction === 'asc' ? -1 : 1;
      }
      if (a[columnSort.column] > b[columnSort.column]) {
        return columnSort.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  const snowflakeProcessing =
    useSnowflakeApi &&
    modelAnalysisComparison.processingStatus === 'PROCESSING';
  const calculatedAt = formatAnalysisTimestamp(
    modelAnalysisComparison.calculatedTime
  );
  const queuedAt = formatAnalysisTimestamp(modelAnalysisComparison.queuedTime);
  const showResultsTable =
    !useSnowflakeApi ||
    !loading ||
    hasSnowflakeTableData(modelAnalysisComparison.modelConfigurationAnalyses);

  const handleCloseModelDetails = () => {
    setDetailsModelConfiguration(null);
  };

  const handleRecalculate = () => {
    if (useSnowflakeApi) {
      clearSnowflakePollTimeout();
      snowflakeSignatureLastChangedAtRef.current = Date.now();
      setSnowflakeRefreshing(true);
      fetchSnowflakeModelAnalysisRef
        .current()
        .then(response => handleSnowflakeResponseRef.current(response))
        .catch(() => scheduleSnowflakePoll());
      return;
    }

    dispatch(recalculateModelAnalysisComparison(sourceType, sourceId));
  };

  const handleShowModelSetModal = modelSet => {
    ws.get(`/model_sets/${modelSet.id}`).then(response => {
      setDetailsModelSet(response.data.result);
    });
  };

  const handleDownload = () => {
    window.location = ws.url(
      `/model_analysis_comparisons/${sourceType}/${sourceId}/download`
    );
  };

  const handleColumnSortChange = (column, direction) => {
    setColumnSort({
      column,
      direction
    });
  };

  const RankLabel = props => {
    const { rank, lowest, primaryRank } = props;
    return (
      <Label
        color={
          lowest
            ? !primaryRank
              ? colors.yellow[800]
              : colors.blue[600]
            : colors.grey[500]
        }>
        {rank}
      </Label>
    );
  };

  const ColumnSort = props => {
    const { label, column } = props;

    return (
      <TableSortLabel
        active={columnSort.column == column}
        direction={columnSort?.direction}
        onClick={() =>
          handleColumnSortChange(
            column,
            columnSort.direction === 'asc' ? 'desc' : 'asc'
          )
        }
        hideSortIcon={columnSort === undefined}>
        {label}{' '}
        {columnSort.column != column && (
          <FontAwesomeIcon className={classes.sortable} icon={faSort} />
        )}
      </TableSortLabel>
    );
  };

  const ProcessingStatus = props => {
    const { modelAnalysisComparison } = props;
    const processingStatus = modelAnalysisComparison.processingStatus;

    if (!processingStatus) return null;

    const statusColors = {
      COMPLETED: colors.green[600],
      PROCESSING: colors.blue[600],
      QUEUED: colors.orange[600],
      ERRORED: colors.red[600]
    };

    return (
      <Label
        color={statusColors[processingStatus]}
        style={{ borderRadius: 12, paddingRight: 0, marginRight: 8 }}>
        {MODEL_ANALYSIS_PROCESSING_STATUS[processingStatus]}
        {processingStatus === 'COMPLETED' && (
          <CompleteIcon
            style={{
              color: 'white',
              height: 14
            }}
          />
        )}
        {processingStatus === 'PROCESSING' && (
          <CircularProgress
            style={{ marginLeft: 4, marginRight: 6 }}
            size={10}
            color={'white'}
          />
        )}
        {processingStatus === 'QUEUED' && (
          <QueuedIcon
            style={{
              color: 'white',
              height: 14
            }}
          />
        )}
        {processingStatus === 'ERRORED' && (
          <ErroredIcon
            style={{
              color: 'white',
              height: 14
            }}
          />
        )}
      </Label>
    );
  };

  return (
    <Card>
      <CardHeader
        title={useSnowflakeApi ? 'Model Analysis (Snowflake)' : 'Model Analysis'}
        subheader={
          !loading && (
            <>
              <>
                {snowflakeRefreshing
                  ? 'Checking Snowflake for updates...'
                  : ''}
              </>

              <>
                {calculatedAt
                  ? 'Last calculated on ' +
                    calculatedAt.format('MMM Do YYYY, h:mm:ss a')
                  : ''}
              </>

              <>
                {modelAnalysisComparison?.processingStatus !== 'ERRORED' &&
                modelAnalysisComparison?.processingStatus !== 'COMPLETED' &&
                lastUpdatedTime
                  ? ' (status last updated at ' +
                    moment(lastUpdatedTime).format('h:mm:ss a') +
                    ', updates every 5 seconds)'
                  : ''}
              </>

              <>
                {modelAnalysisComparison.eventCellModels &&
                  modelAnalysisComparison.eventCellModels.length > 0 && (
                    <>
                      . Walk Cell Types:{' '}
                      {modelAnalysisComparison.eventCellModels
                        .map(cm => CELL_MODELS[cm])
                        .join(', ')}
                    </>
                  )}
              </>
              <>
                {modelAnalysisComparison.eventCellModels &&
                  modelAnalysisComparison.eventCellModels.length > 0 && (
                    <>
                      . Score:{' '}
                      {modelAnalysisComparison.subModel >= 0
                        ? DEFAULT_SUB_MODEL_AREA_DESCRIPTIONS[
                            modelAnalysisComparison.subModel
                          ]
                        : 'Combined'}
                    </>
                  )}
              </>
              <div>
                {!useSnowflakeApi &&
                  !modelAnalysisComparison.calculating &&
                  modelAnalysisComparison.analysisEventCountsConsistent &&
                  firstAnalysis &&
                  (displaySessionCount
                    ? firstAnalysis.sessionCount +
                      ' Test Session' +
                      (firstAnalysis.sessionCount !== 1 ? 's' : '') +
                      ', '
                    : '') +
                    (displayCustomerEventCount
                      ? firstAnalysis.customerEventCount +
                        ' Customer Event' +
                        (firstAnalysis.customerEventCount !== 1 ? 's' : '') +
                        ', '
                      : '') +
                    firstAnalysis.eventCount +
                    ' Walk' +
                    (firstAnalysis.eventCount !== 1 ? 's' : '') +
                    ' ' +
                    '(' +
                    firstAnalysis.objectEventCount +
                    ' Object, ' +
                    firstAnalysis.noObjectEventCount +
                    ' No Object)'}
              </div>
              <>
                {modelAnalysisComparison.calculated &&
                  !modelAnalysisComparison.calculating &&
                  !modelAnalysisComparison.analysisEventCountsConsistent && (
                    <>
                      <InfoIcon className={classes.subHeaderInfoIcon} />
                      Inconsistent Model Event Counts
                    </>
                  )}
              </>
            </>
          )
        }
        action={
          <>
            <Button
              size="small"
              className={classes.actionButton}
              variant="outlined"
              color="primary"
              onClick={handleDownload}
              disabled={
                !modelAnalysisComparison.calculatedTime ||
                loading ||
                (modelAnalysisComparison.processingStatus === 'PROCESSING' &&
                  !modelAnalysisComparison.calculatedTime)
              }>
              Download
            </Button>
            <Button
              size="small"
              className={classes.actionButton}
              variant="outlined"
              color="primary"
              onClick={handleRecalculate}
              disabled={
                modelAnalysisComparison.processingStatus === 'PROCESSING' ||
                modelAnalysisComparison.processingStatus === 'QUEUED' ||
                loading ||
                snowflakeRefreshing
              }>
              Recalculate
            </Button>
          </>
        }
      />
      <Divider />
      <CardContent className={classes.content}>
        {(loading || snowflakeRefreshing) && <LinearProgress />}

        {modelAnalysisComparison && !showResultsTable && loading && (
          <div style={{ padding: 16 }}>
            Loading model analysis from Snowflake...
          </div>
        )}

        {modelAnalysisComparison && showResultsTable && (
          <PerfectScrollbar>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={4}>
                    {' '}
                    <ProcessingStatus
                      modelAnalysisComparison={modelAnalysisComparison}
                    />{' '}
                    {modelAnalysisComparison.processingStatus ===
                      'PROCESSING' &&
                      (useSnowflakeApi ? (
                        <>Calculating from Snowflake...</>
                      ) : (
                        <>
                          <NumberFormat
                            value={
                              modelAnalysisComparison.processingCompletionPercentage >
                              0.99
                                ? 0.99
                                : modelAnalysisComparison.processingCompletionPercentage
                            }
                            percentage={true}
                            fixedDecimalScale={true}
                            precision={0}
                            suffix={'% Complete'}
                          />

                          {' (' + completeCount + ' of ' + totalCount + ')'}
                        </>
                      ))}
                    {modelAnalysisComparison.processingStatus === 'QUEUED' && (
                      <>
                        {'Queued on'}{' '}
                        {queuedAt
                          ? queuedAt.format('MMM Do YYYY, h:mm a')
                          : ''}
                        {modelAnalysisComparison.queuePosition
                          ? ', Position ' +
                            modelAnalysisComparison.queuePosition
                          : ''}
                      </>
                    )}
                  </TableCell>
                  <TableCell colSpan={3} className={classes.columnSeparator}>
                    Score Components
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    Object Present
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    No Object
                  </TableCell>
                  <TableCell colSpan={7} className={classes.columnSeparator} />
                </TableRow>
                <TableRow>
                  <TableCell>
                    <Search
                      style={{ minWidth: 200 }}
                      autoFocus={true}
                      placeholder="Model/Model Set"
                      value={modelSearch}
                      onSearchValueChange={value => setModelSearch(value)}
                      className={classes.search}
                      hideSearchButton={true}
                      onSearch={() => {}}
                    />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    Walks
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort label="Rank" column="scoreRank" />
                  </TableCell>
                  <TableCell>
                    <ColumnSort label="Score" column="score" />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort
                      label="PR AUC"
                      column="precisionRecallAreaUnderCurve"
                    />
                  </TableCell>
                  <TableCell>
                    <ColumnSort
                      label="Sum Total"
                      column="normalizedOptimalAccuracySumBias"
                    />
                  </TableCell>
                  <TableCell>
                    <ColumnSort
                      label="Accuracy Probability"
                      column="breakevenSensitivityProbability"
                    />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort
                      label="Accuracy"
                      column="objectPresentAccuracy"
                    />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort label="Accuracy" column="noObjectAccuracy" />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort
                      label="Obj Avg"
                      column="objectPresentAverageScore"
                    />
                  </TableCell>
                  <TableCell>
                    <ColumnSort
                      label="No-obj Avg"
                      column="noObjectAverageScore"
                    />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort label="Std Dev" column="standardDeviation" />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort label="ROC AUC" column="rocAreaUnderCurve" />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort
                      label="Num Std Dev"
                      column="objectPresentNumStandardDeviations"
                    />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort
                      label="Sensitivity"
                      column="standardDeviationBreakevenBias"
                    />
                  </TableCell>
                  <TableCell className={classes.columnSeparator}>
                    <ColumnSort label="Margin Spread" column="marginSpread" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAnalyses.map(modelConfigurationAnalysis => (
                  <TableRow
                    className={
                      modelConfigurationAnalysis.asWalkedAnalysis
                        ? classes.asWalkedRow
                        : modelConfigurationAnalysis.modelSet
                        ? classes.modelSetRow
                        : ''
                    }
                    hover={modelConfigurationAnalysis.eventCount > 0}
                    key={
                      modelConfigurationAnalysis.modelConfiguration
                        ? 'model-' +
                          modelConfigurationAnalysis.modelConfiguration?.id
                        : modelConfigurationAnalysis.modelSet
                        ? 'model-set-' + modelConfigurationAnalysis.modelSet?.id
                        : 'as-walked'
                    }>
                    <TableCell className={classes.modelName}>
                      {modelConfigurationAnalysis.modelConfiguration && (
                        <Link
                          color="inherit"
                          target="_blank"
                          component={RouterLink}
                          to={
                            '/models/model_configurations/' +
                            modelConfigurationAnalysis.modelConfiguration?.id +
                            '/collection_analysis'
                          }
                          variant="h6">
                          {
                            modelConfigurationAnalysis.modelConfiguration
                              .displayName ||
                            modelConfigurationAnalysis.modelConfiguration.name
                          }
                        </Link>
                      )}
                      {modelConfigurationAnalysis.modelSet && (
                        <Link
                          color="inherit"
                          target="_blank"
                          component={RouterLink}
                          to={
                            '/models/model_sets/' +
                            modelConfigurationAnalysis.modelSet?.id
                          }
                          variant="h6">
                          Model Set: {modelConfigurationAnalysis.modelSet.name}
                        </Link>
                      )}
                      {modelConfigurationAnalysis.asWalkedAnalysis && (
                        <>
                          As Walked{' '}
                          {modelConfigurationAnalysis.asWalkedModelNames && (
                            <Tooltip
                              title={
                                <ul>
                                  {modelConfigurationAnalysis.asWalkedModelNames?.map(
                                    name => (
                                      <li>{name}</li>
                                    )
                                  )}
                                </ul>
                              }>
                              <InfoIcon className={classes.asWalkedInfoIcon} />
                            </Tooltip>
                          )}
                        </>
                      )}
                      {modelConfigurationAnalysis.modelSet && (
                        <InfoIcon
                          onClick={() =>
                            handleShowModelSetModal(
                              modelConfigurationAnalysis.modelSet
                            )
                          }
                          className={classes.modelSetInfoIcon}
                        />
                      )}
                      {!modelAnalysisComparison.calculating &&
                        !modelAnalysisComparison.analysisEventCountsConsistent &&
                        modelAnalysisComparison.modelConfiguration && (
                          <>
                            <div className={classes.modelEventCount}>
                              Model created/updated:{' '}
                              {modelConfigurationAnalysis.calculated &&
                                moment(
                                  modelConfigurationAnalysis.modelConfiguration
                                    .lastUpdatedTime
                                    ? modelConfigurationAnalysis
                                        .modelConfiguration.lastUpdatedTime
                                    : modelConfigurationAnalysis
                                        .modelConfiguration.createdTime
                                ).format('MMM Do YYYY, h:mm a')}
                            </div>
                            <div className={classes.modelEventCount}>
                              {modelConfigurationAnalysis.calculated &&
                                modelConfigurationAnalysis.eventCount +
                                  ' Walk' +
                                  (modelConfigurationAnalysis.eventCount !== 1
                                    ? 's'
                                    : '')}
                              {modelConfigurationAnalysis.calculated &&
                                (modelConfigurationAnalysis.modelConfiguration
                                  .supportedCellModels
                                  ? ', Supported Cells: ' +
                                    modelConfigurationAnalysis.modelConfiguration.supportedCellModels
                                      .map(cm => CELL_MODELS[cm])
                                      .join(', ')
                                  : '')}
                            </div>
                          </>
                        )}
                    </TableCell>
                    {modelConfigurationAnalysis.calculated ? (
                      <>
                        {!modelConfigurationAnalysis.eventCount ? (
                          <TableCell
                            className={clsx(
                              classes.columnSeparator,
                              classes.modelEventCount
                            )}
                            colSpan={14}>
                            Insufficient supported data available
                          </TableCell>
                        ) : (
                          <>
                            <TableCell className={classes.columnSeparator}>
                              <NumberFormat
                                value={modelConfigurationAnalysis.eventCount}
                              />
                            </TableCell>
                            <TableCell className={classes.columnSeparator}>
                              {/* rank */}
                              <RankLabel
                                primaryRank={true}
                                rank={modelConfigurationAnalysis.scoreRank}
                                lowest={
                                  modelConfigurationAnalysis.lowestScoreRank
                                }
                              />
                            </TableCell>
                            {!modelConfigurationAnalysis.breakevenWalksAvailable ? (
                              <TableCell
                                className={clsx(
                                  classes.columnSeparator,
                                  classes.modelEventCount
                                )}
                                colSpan={6}>
                                Insufficient gun/no-gun walks for breakeven bias
                                based calculations
                              </TableCell>
                            ) : (
                              <>
                                <TableCell>
                                  {/* score */}
                                  <NumberFormat
                                    percentage={false}
                                    fixedDecimalScale={true}
                                    precision={4}
                                    value={modelConfigurationAnalysis.score}
                                    suffix={''}
                                  />
                                </TableCell>
                                <TableCell className={classes.columnSeparator}>
                                  {/* Precision Recall AUC */}
                                  <NumberFormat
                                    percentage={false}
                                    fixedDecimalScale={true}
                                    precision={4}
                                    value={
                                      modelConfigurationAnalysis.precisionRecallAreaUnderCurve
                                    }
                                    suffix={''}
                                  />
                                </TableCell>
                                <TableCell>
                                  {/* Sum Total */}
                                  <NumberFormat
                                    percentage={false}
                                    fixedDecimalScale={true}
                                    precision={4}
                                    value={
                                      modelConfigurationAnalysis.normalizedOptimalAccuracySumBias
                                    }
                                    suffix={''}
                                  />
                                </TableCell>
                                <TableCell>
                                  {/* Breakeven Sensitivity Probability */}
                                  <NumberFormat
                                    percentage={false}
                                    fixedDecimalScale={true}
                                    precision={4}
                                    value={
                                      modelConfigurationAnalysis.breakevenSensitivityProbability
                                    }
                                    suffix={''}
                                  />
                                </TableCell>
                                <TableCell className={classes.columnSeparator}>
                                  {/* objectPresentAccuracy */}
                                  <NumberFormat
                                    percentage={true}
                                    fixedDecimalScale={true}
                                    precision={2}
                                    value={
                                      modelConfigurationAnalysis.objectPresentAccuracy
                                    }
                                    suffix={'%'}
                                  />
                                </TableCell>
                                <TableCell className={classes.columnSeparator}>
                                  {/* noObjectAccuracy */}
                                  <NumberFormat
                                    percentage={true}
                                    fixedDecimalScale={true}
                                    precision={2}
                                    value={
                                      modelConfigurationAnalysis.noObjectAccuracy
                                    }
                                    suffix={'%'}
                                  />
                                </TableCell>
                              </>
                            )}
                            <TableCell className={classes.columnSeparator}>
                              <NumberFormat
                                percentage={true}
                                fixedDecimalScale={true}
                                precision={2}
                                value={
                                  modelConfigurationAnalysis.objectPresentAverageScore
                                }
                                suffix={'%'}
                              />
                            </TableCell>
                            <TableCell>
                              <NumberFormat
                                percentage={true}
                                fixedDecimalScale={true}
                                precision={2}
                                value={
                                  modelConfigurationAnalysis.noObjectAverageScore
                                }
                                suffix={'%'}
                              />
                            </TableCell>
                            <TableCell className={classes.columnSeparator}>
                              <NumberFormat
                                percentage={true}
                                fixedDecimalScale={true}
                                precision={2}
                                value={
                                  modelConfigurationAnalysis.standardDeviation
                                }
                                suffix={''}
                              />
                            </TableCell>
                            {!modelConfigurationAnalysis.breakevenWalksAvailable ? (
                              <TableCell
                                className={classes.columnSeparator}
                                colSpan={4}
                              />
                            ) : (
                              <>
                                <TableCell className={classes.columnSeparator}>
                                  {/* ROC AUC */}
                                  <NumberFormat
                                    percentage={false}
                                    fixedDecimalScale={true}
                                    precision={4}
                                    value={
                                      modelConfigurationAnalysis.rocAreaUnderCurve
                                    }
                                    suffix={''}
                                  />
                                </TableCell>
                                <TableCell className={classes.columnSeparator}>
                                  {/* objectPresentStandardDeviations */}
                                  <NumberFormat
                                    percentage={false}
                                    fixedDecimalScale={true}
                                    precision={2}
                                    value={
                                      modelConfigurationAnalysis.objectPresentNumStandardDeviations
                                    }
                                    suffix={''}
                                  />
                                </TableCell>
                                <TableCell className={classes.columnSeparator}>
                                  {' '}
                                  {/* standardDeviationBreakevenBias */}
                                  <NumberFormat
                                    percentage={true}
                                    fixedDecimalScale={true}
                                    precision={2}
                                    value={
                                      modelConfigurationAnalysis.standardDeviationBreakevenBias
                                    }
                                    suffix={''}
                                  />
                                </TableCell>
                                <TableCell className={classes.columnSeparator}>
                                  {/* marginSpread */}
                                  <NumberFormat
                                    percentage={true}
                                    fixedDecimalScale={true}
                                    precision={2}
                                    value={
                                      modelConfigurationAnalysis.marginSpread
                                    }
                                    suffix={''}
                                  />
                                  <span className={classes.marginDetail}>
                                    (
                                    <NumberFormat
                                      percentage={true}
                                      fixedDecimalScale={true}
                                      precision={2}
                                      value={
                                        modelConfigurationAnalysis.objectPresentAverageMargin
                                      }
                                      suffix={''}
                                    />
                                    /
                                    <NumberFormat
                                      percentage={true}
                                      fixedDecimalScale={true}
                                      precision={2}
                                      value={
                                        modelConfigurationAnalysis.noObjectAverageMargin
                                      }
                                      suffix={''}
                                    />
                                    )
                                  </span>
                                </TableCell>
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <TableCell
                        className={classes.columnSeparator}
                        colSpan={14}>
                        <CircularProgress size={10} />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {!loading && sortedAnalyses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={14}>No data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </PerfectScrollbar>
        )}
      </CardContent>
      <ModelConfigurationDetails
        modelConfiguration={detailsModelConfiguration}
        onClose={handleCloseModelDetails}
      />
      <ModelSetModal
        onClose={() => setDetailsModelSet(null)}
        modelSet={detailsModelSet}
      />
    </Card>
  );
};

ModelAnalysisComparison.propTypes = {
  sourceId: PropTypes.string.isRequired,
  sourceType: PropTypes.string.isRequired,
  useSnowflakeApi: PropTypes.bool
};

export default ModelAnalysisComparison;
